/**
 * '@chat-config' — the ONE sanctioned doorway from the chat core into
 * audience land (#1731, spec §4.1; TRF port of cpv3's '@brand/chat-config').
 *
 * TRF ships one bundle for two audiences, so instead of a static per-build
 * object this module resolves a config per audience at runtime. A mount site
 * does:
 *
 *   <ChatSurfaceProvider config={resolveChatConfig('companion', { agentName })}>
 *
 * with the provider and `useChatSurface()` imported from '@core'. The core's
 * plain accessors (`chatConfig()` / `chatCopy()`) fall back to
 * `defaultChatConfig` — the companion audience — when no provider is
 * mounted, which is what the ported unit suites rely on.
 *
 * Capability flags are load-bearing (spec §1.2, §10.8): a `false` hides the
 * surface; flipping one ships with its copy slot and its endpoint.
 */
import i18n from 'i18next';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { BrandChatConfig } from '@core/config/types';
import { companionChatConfig } from './companion';
import { activeLanguage } from './copy/companion';
import { trferChatConfig } from './trfer';

export type ChatAudience = 'companion' | 'trfer';
export type ChatSurfaceConfig = BrandChatConfig;

export interface ResolveChatConfigOptions {
  /**
   * The rendered agent name. The companion name is per family (persona
   * preference, `useCompanionName()`), which a static module cannot read —
   * the mount site passes it. Defaults to the platform companion name;
   * ignored for the trfer audience, whose agent is always Williams.
   */
  agentName?: string;
  /**
   * #2109 R3-B24: the UI language the companion pack is written in. Defaults
   * to the language the i18n runtime resolved. Ignored for the trfer
   * audience, whose pack stays English until R4.
   */
  language?: string;
}

/**
 * #2109 R3-B24: one config per (audience, name, language), so a mount site
 * that memoises on the result re-renders exactly when one of the three
 * changes — and a language switch is one of them. The last key element
 * records whether that language’s `chat` catalog had arrived: a config built
 * while the lazy chunk was still loading carries English fallbacks, and it
 * must not be served again once the real strings are there.
 */
const cache = new Map<string, ChatSurfaceConfig>();

function hasChatCatalog(language: string): boolean {
  return i18n.hasResourceBundle(language, 'chat');
}

export function resolveChatConfig(
  audience: ChatAudience,
  options: ResolveChatConfigOptions = {},
): ChatSurfaceConfig {
  switch (audience) {
    case 'companion': {
      const language = options.language ?? activeLanguage();
      const key = `companion|${options.agentName ?? ''}|${language}|${hasChatCatalog(language) ? 1 : 0}`;
      let cfg = cache.get(key);
      if (!cfg) {
        cfg = companionChatConfig(options.agentName, language);
        cache.set(key, cfg);
      }
      return cfg;
    }
    case 'trfer': {
      let cfg = cache.get('trfer');
      if (!cfg) {
        cfg = trferChatConfig();
        cache.set('trfer', cfg);
      }
      return cfg;
    }
    default: {
      const never: never = audience;
      throw new Error(`[chat-config] unknown audience: ${String(never)}`);
    }
  }
}

/**
 * The hook a mount site uses in place of `useMemo(() => resolveChatConfig(…))`
 * (#2109 R3-B24). It subscribes to the i18n runtime, so a language switch — or
 * the lazy `chat` catalog arriving — hands the `ChatSurfaceProvider` a new
 * config and every core surface under it re-renders in the new language
 * without a reload. `useSuspense: false`: the mount site keeps rendering (in
 * English) while a target catalog loads, rather than suspending the page.
 */
export function useChatConfig(
  audience: ChatAudience,
  options: ResolveChatConfigOptions = {},
): ChatSurfaceConfig {
  const { i18n: runtime, ready } = useTranslation('chat', { useSuspense: false });
  const language = options.language ?? runtime.resolvedLanguage ?? runtime.language ?? 'en';
  const loaded = ready && runtime.hasResourceBundle(language, 'chat');
  const { agentName } = options;
  return useMemo(
    () => resolveChatConfig(audience, { agentName, language }),
    // `loaded` is a dependency on purpose: it flips when the catalog lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [audience, agentName, language, loaded],
  );
}

/**
 * What the core's plain accessors read when no ChatSurfaceProvider is
 * mounted: the companion audience in the CURRENT language.
 *
 * #2109 R3-B24: this used to be a module-level constant, which froze the pack
 * in whatever language was active when the module first loaded (and, loaded
 * before the i18n runtime initialised, would have frozen raw keys). It is now
 * a live view: every property read resolves the current config through the
 * memo above. Identity is stable (`chatConfig()` returns this same object), and
 * spreading it (`{ ...defaultChatConfig }`) copies the current values.
 */
export const defaultChatConfig: ChatSurfaceConfig = new Proxy({} as ChatSurfaceConfig, {
  get: (_target, prop) => Reflect.get(resolveChatConfig('companion'), prop),
  has: (_target, prop) => Reflect.has(resolveChatConfig('companion'), prop),
  ownKeys: () => Reflect.ownKeys(resolveChatConfig('companion')),
  getOwnPropertyDescriptor: (_target, prop) => {
    const descriptor = Reflect.getOwnPropertyDescriptor(resolveChatConfig('companion'), prop);
    return descriptor ? { ...descriptor, configurable: true } : undefined;
  },
});
